import cn from 'classnames';
import styled from 'styled-components';

import { WidgetContainer } from '@/features/vfo/components';
import { ApiDocumentResponseV2 } from '@/shared/api';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

import { useCases } from '../../hooks/useCases';
import { useOpenDocument } from '../../hooks/useOpenDocument';
import { VfoFileIcon } from '../VfoFileIcon';

type DocumentsGridProps = {
  files: ApiDocumentResponseV2[];
  caseId?: string;
};

export const DocumentsGrid: BFC<DocumentsGridProps> = ({ files, caseId, ...props }) => {
  const { t } = useTranslation('features/vfo', {
    keyPrefix: 'documents',
  });
  const { t: tc } = useTranslation('features/vfo', {
    keyPrefix: `documents.${caseId ? 'caseDocuments' : 'allDocuments'}`,
  });

  const modals = useModalsStore();
  const router = useAppRouter();
  const { cases } = useCases();
  const openDocument = useOpenDocument();

  const displayedFiles = files.slice(0, 9);

  if (displayedFiles.length === 0) {
    return (
      <WidgetContainerStyled {...props}>
        <Header>
          <TitleWrapper>
            <Title>{tc('title')}</Title>
            <span>(0)</span>
          </TitleWrapper>
        </Header>
        <Column>
          <Column style={{ gap: 12 }}>
            <Icon style={{ color: COLORS.systemDark100 }} name="users-three" size="s44" />
            <EmptyText>{tc('noDocuments')}</EmptyText>
          </Column>
          {/* not counting the private case */}
          {cases.length > 1 && (
            <Button
              type="default"
              onClick={() => {
                if (caseId) {
                  modals.openModal({
                    type: 'SelectDocumentsToCaseModal',
                    caseId,
                  });
                  return;
                }
                modals.openModal({
                  type: 'UploadVfoDocumentModal',
                  caseId,
                });
              }}
            >
              {tc('addNewBtn')}
            </Button>
          )}
        </Column>
      </WidgetContainerStyled>
    );
  }

  return (
    <WidgetContainerStyled {...props}>
      <Header>
        <TitleWrapper>
          <Title>{tc('title')}</Title>
          <span>({files.length})</span>
        </TitleWrapper>
        <LinkStyled
          type="text"
          size="middle"
          onClick={() => {
            router.push(caseId ? `/vfo/case/${caseId}/documents` : '/vfo/documents', undefined, {
              shallow: true,
            });
          }}
        >
          {t('viewAll')}
        </LinkStyled>
      </Header>
      <GridContainer className="uikitScrollbar">
        {displayedFiles.map((file) => (
          <GridItem key={file.id}>
            <FileTypeWrapper
              className={cn({ isExternal: file.source === 'EXTERNAL' })}
              onClick={() => openDocument(file)}
            >
              <VfoFileIcon size="s20" file={file} />
            </FileTypeWrapper>
            <Text>{file.title}</Text>
          </GridItem>
        ))}
      </GridContainer>
    </WidgetContainerStyled>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
`;

const Title = styled.span`
  font-weight: 600;
`;

const LinkStyled = styled(Button)`
  &&& {
    color: ${COLORS.systemSky200};
    font-weight: 700;
    padding: 0;
    height: auto;

    &:hover {
      color: ${COLORS.systemSky200};
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  row-gap: 32px;
  overflow: auto;
  max-height: 100%;

  ${media.sm} {
    grid-template-columns: 1fr;
    padding-bottom: 32px;
  }
`;

const GridItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyText = styled.span`
  color: ${COLORS.systemDark200};
  font-weight: 600;
`;

const FileTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.systemDark200};
  border-radius: 4px;
  width: 28px;
  height: 36px;
  color: ${COLORS.white};
  position: relative;
  flex-shrink: 0;
  cursor: pointer;

  &.isExternal {
    background: ${COLORS.systemSky300};
  }
`;

const Text = styled.span`
  font-size: 12px;
  line-height: 16px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-height: 40px;
`;

const WidgetContainerStyled = styled(WidgetContainer)`
  max-height: 270px;
  overflow: hidden;
  container-type: inline-size;
  container-name: main;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
`;
